import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import AdminCustomers from './AdminCustomers';
import AdminOrders from './AdminOrders';
import AdminAnalytics from './AdminAnalytics';
import AdminSettings from './AdminSettings';

export default function AdminPanel() {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const navigate = useNavigate();

  const API_URL = 'https://budget-app-server-two.vercel.app/api';

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      verifyAdminToken(token);
    }
  }, []);

  const verifyAdminToken = async (token) => {
    try {
      const response = await axios.get(API_URL + '/admin/dashboard', {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (response.data) {
        setIsAuthenticated(true);
        setAdminData(response.data);
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('adminToken');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(API_URL + '/admin/login', {
        email: 'admin@finovatrack.com',
        password: password
      });
      
      if (response.data.success) {
        localStorage.setItem('adminToken', response.data.token);
        setIsAuthenticated(true);
        const dashboardResponse = await axios.get(API_URL + '/admin/dashboard', {
          headers: { Authorization: 'Bearer ' + response.data.token }
        });
        setAdminData(dashboardResponse.data);
        toast.success('Admin login successful!');
      } else {
        toast.error('Invalid admin password');
        setPassword('');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid admin credentials');
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setAdminData(null);
    toast.success('Logged out');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="text-center text-3xl font-extrabold text-white">Admin Panel</h2>
            <p className="mt-2 text-center text-sm text-indigo-200">Enter admin password to continue</p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder="Enter admin password"
                  disabled={loading}
                />
              </div>
            </div>
            <div>
              <button type="submit" disabled={loading} className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50">
                {loading ? 'Verifying...' : 'Access Admin Panel'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  const totalUsers = adminData?.totalUsers || 0;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Admin Header */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-8">
              <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Admin Dashboard</h1>
              <div className="flex space-x-4">
                <button onClick={() => setActiveTab('dashboard')} className={'px-3 py-2 text-sm font-medium rounded-md transition-colors ' + (activeTab === 'dashboard' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700 hover:bg-gray-100')}>Dashboard</button>
                <button onClick={() => setActiveTab('customers')} className={'px-3 py-2 text-sm font-medium rounded-md transition-colors ' + (activeTab === 'customers' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700 hover:bg-gray-100')}>Customers</button>
                <button onClick={() => setActiveTab('orders')} className={'px-3 py-2 text-sm font-medium rounded-md transition-colors ' + (activeTab === 'orders' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700 hover:bg-gray-100')}>Orders</button>
                <button onClick={() => setActiveTab('analytics')} className={'px-3 py-2 text-sm font-medium rounded-md transition-colors ' + (activeTab === 'analytics' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700 hover:bg-gray-100')}>Analytics</button>
                <button onClick={() => setActiveTab('settings')} className={'px-3 py-2 text-sm font-medium rounded-md transition-colors ' + (activeTab === 'settings' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700 hover:bg-gray-100')}>Settings</button>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Admin: {adminData?.users?.find(u => u.role === 'admin')?.username || 'Admin'}</span>
              <button onClick={handleLogout} className="text-red-600 hover:text-red-800">Logout</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {activeTab === 'dashboard' && (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
              <div className="bg-white overflow-hidden shadow rounded-lg p-5">
                <div className="flex items-center"><div className="flex-shrink-0"><svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg></div><div className="ml-5 w-0 flex-1"><dl><dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt><dd className="text-lg font-medium text-gray-900">{totalUsers}</dd></dl></div></div>
              </div>
              <div className="bg-white overflow-hidden shadow rounded-lg p-5"><div className="flex items-center"><div className="flex-shrink-0"><svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg></div><div className="ml-5 w-0 flex-1"><dl><dt className="text-sm font-medium text-gray-500 truncate">Total Orders</dt><dd className="text-lg font-medium text-gray-900">156</dd></dl></div></div></div>
              <div className="bg-white overflow-hidden shadow rounded-lg p-5"><div className="flex items-center"><div className="flex-shrink-0"><svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z" /></svg></div><div className="ml-5 w-0 flex-1"><dl><dt className="text-sm font-medium text-gray-500 truncate">Total Revenue</dt><dd className="text-lg font-medium text-gray-900">,300</dd></dl></div></div></div>
              <div className="bg-white overflow-hidden shadow rounded-lg p-5"><div className="flex items-center"><div className="flex-shrink-0"><svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg></div><div className="ml-5 w-0 flex-1"><dl><dt className="text-sm font-medium text-gray-500 truncate">Active Subscriptions</dt><dd className="text-lg font-medium text-gray-900">89</dd></dl></div></div></div>
            </div>
            <div className="bg-white shadow overflow-hidden sm:rounded-md"><div className="px-4 py-5 border-b border-gray-200 sm:px-6"><h3 className="text-lg leading-6 font-medium text-gray-900">Recent Users</h3></div><ul className="divide-y divide-gray-200">{adminData?.users?.slice(0, 5).map((user) => (<li key={user._id} className="px-4 py-4 sm:px-6"><div className="flex items-center justify-between"><div className="flex items-center"><div className="flex-shrink-0"><div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center"><span className="text-indigo-600 font-medium">{user.username?.charAt(0).toUpperCase()}</span></div></div><div className="ml-4"><div className="text-sm font-medium text-gray-900">{user.username}</div><div className="text-sm text-gray-500">{user.email}</div></div></div><div className="flex items-center space-x-2"><span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">user</span><div className="text-sm text-gray-500">Joined: {new Date(user.createdAt).toLocaleDateString()}</div></div></div></li>))}</ul></div>
          </>
        )}
        {activeTab === 'customers' && <AdminCustomers />}
        {activeTab === 'orders' && <AdminOrders />}
        {activeTab === 'analytics' && <AdminAnalytics />}
        {activeTab === 'settings' && <AdminSettings />}
      </div>
    </div>
  );
}
